import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const XmlFormatter: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [indentSize, setIndentSize] = useState(2);

  const formatXml = (xml: string, indent: number = 2): string => {
    let formatted = '';
    let indentLevel = 0;
    const indentStr = ' '.repeat(indent);
    
    // Remove all whitespace between tags
    xml = xml.replace(/>\s+</g, '><');
    
    // Split by tags
    const parts = xml.split(/(<\/?[^>]+>)/);
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (!part) continue;
      
      if (part.startsWith('</')) {
        // Closing tag
        indentLevel--;
        formatted += indentStr.repeat(Math.max(0, indentLevel)) + part + '\n';
      } else if (part.startsWith('<') && !part.endsWith('/>')) {
        // Opening tag
        formatted += indentStr.repeat(indentLevel) + part + '\n';
        indentLevel++;
      } else {
        // Self-closing tag or text content
        formatted += indentStr.repeat(indentLevel) + part + '\n';
      }
    }
    
    return formatted.trim();
  };

  const formatXmlHandler = () => {
    try {
      setError('');
      const formatted = formatXml(input, indentSize);
      setOutput(formatted);
    } catch (err) {
      setError('XML 格式化失败');
      setOutput('');
    }
  };

  const compressXml = () => {
    try {
      setError('');
      const compressed = input.replace(/>\s+</g, '><').replace(/\s+/g, ' ').trim();
      setOutput(compressed);
    } catch (err) {
      setError('XML 压缩失败');
      setOutput('');
    }
  };

  const validateXml = () => {
    try {
      setError('');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(input, 'text/xml');
      
      if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
        setError('❌ XML 格式无效');
        setOutput('');
      } else {
        setOutput('✅ XML 格式有效');
      }
    } catch (err) {
      setError('❌ XML 格式无效');
      setOutput('');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            返回首页
          </Link>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">XML 在线格式化</h1>
          <p className="text-gray-600">格式化、压缩、验证 XML 数据</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <button
              onClick={formatXmlHandler}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              格式化
            </button>
            <button
              onClick={compressXml}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              压缩
            </button>
            <button
              onClick={validateXml}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              验证
            </button>
            <button
              onClick={clearAll}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              清空
            </button>
            <div className="flex items-center">
              <label className="mr-2 text-gray-700">缩进:</label>
              <select
                value={indentSize}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1"
              >
                <option value={2}>2 空格</option>
                <option value={4}>4 空格</option>
                <option value={8}>8 空格</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Input/Output */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">输入 XML</h3>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="请输入要格式化的 XML 数据..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Output */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">输出结果</h3>
              {output && (
                <button
                  onClick={copyToClipboard}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors"
                >
                  复制
                </button>
              )}
            </div>
            <textarea
              value={output}
              readOnly
              placeholder="格式化后的结果将显示在这里..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
            />
          </div>
        </div>

        {/* Example */}
        <div className="mt-8 bg-green-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">示例 XML</h3>
          <button
            onClick={() => setInput(`<?xml version="1.0" encoding="UTF-8"?>
<bookstore>
<book category="fiction">
<title>哈利波特</title>
<author>J.K.罗琳</author>
<year>1997</year>
<price>29.99</price>
</book>
<book category="non-fiction">
<title>编程艺术</title>
<author>张三</author>
<year>2023</year>
<price>49.99</price>
</book>
</bookstore>`)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            加载示例
          </button>
        </div>
      </div>
    </div>
  );
};

export default XmlFormatter;
